/**
 * Soporte (1.0 AdminSupport.jsx): colas por estado (abiertos = escalados por el bot o por el
 * usuario, más antiguos primero), conversación completa y respuesta del agente
 * (`support-agentReply`, que avisa al usuario y fija la primera respuesta humana para la promesa).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;
const STATUSES = ['open', 'in_progress', 'waiting_user', 'resolved', 'closed'] as const;
type Status = (typeof STATUSES)[number];

function Thread({ ticket, onDone }: { ticket: Doc; onDone: () => void }): ReactElement {
  const [messages, setMessages] = useState<Doc[] | null>(null);
  const [text, setText] = useState('');
  const [status, setStatus] = useState<Status | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = str(ticket.id);
  const load = (): void => {
    getDocs(
      query(
        collection(db, 'supportMessages'),
        where('ticket_id', '==', id),
        orderBy('created_at', 'asc'),
      ),
    )
      .then((s) => {
        setMessages(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setMessages([]);
      });
  };
  useEffect(load, [id]);
  const send = (): void => {
    if (!text.trim() && !status) return;
    setBusy(true);
    setError(null);
    call(api.support.agentReply, {
      ticket_id: id,
      ...(text.trim() ? { message: text.trim() } : {}),
      ...(status ? { status } : {}),
    })
      .then(() => {
        setText('');
        setStatus('');
        load();
        onDone();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <div style={{ ...card, display: 'grid', gap: 10 }}>
      <div>
        <strong>{str(ticket.subject_es) || str(ticket.subject)}</strong>
        <div style={muted}>
          {str(ticket.category)} · {str(ticket.status)} · idioma {str(ticket.language)} · usuario{' '}
          <Link to={`/usuarios/${str(ticket.uid)}`}>{str(ticket.uid)}</Link>
          {typeof ticket.order_id === 'string' && ticket.order_id ? (
            <>
              {' '}
              · pedido <Link to={`/pedidos/${ticket.order_id}`}>{ticket.order_id}</Link>
            </>
          ) : null}
          {ticket.bot_active === true ? ' · bot activo' : ''}
        </div>
      </div>
      <Notice text={error} error />
      {!messages && <p style={muted}>Cargando…</p>}
      <div style={{ display: 'grid', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
        {(messages ?? []).map((m) => (
          <div
            key={str(m.id)}
            style={{
              padding: '8px 10px',
              borderRadius: 10,
              background:
                m.sender_role === 'user'
                  ? '#F3F4F6'
                  : m.sender_role === 'bot'
                    ? '#EEF2FF'
                    : '#ECFDF5',
              fontSize: 13,
              whiteSpace: 'pre-wrap',
            }}
          >
            <div style={{ ...muted, fontSize: 11 }}>
              {str(m.sender_role)} · {when(m.created_at)}
            </div>
            {/* El equipo lee en español: traducción del usuario/bot, o el original que escribió el agente. */}
            {str(m.content_es) || str(m.content)}
            {str(m.content_es) && (
              <details style={{ ...muted, fontSize: 11, marginTop: 4 }}>
                <summary style={{ cursor: 'pointer' }}>
                  {m.sender_role === 'agent'
                    ? `Enviado al usuario en su idioma (${str(ticket.language)})`
                    : `Original (${str(ticket.language)})`}
                </summary>
                {str(m.content)}
              </details>
            )}
            {Array.isArray(m.attachments) && m.attachments.length > 0 && (
              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                {(m.attachments as string[]).map((u) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer">
                    <img
                      src={u}
                      alt=""
                      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }}
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <textarea
        style={{ ...field, minHeight: 90 }}
        placeholder="Respuesta en español: al usuario le llega traducida a su idioma."
        value={text}
        onChange={(e) => {
          setText(e.target.value);
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          style={{ ...field, width: 200 }}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as Status | '');
          }}
        >
          <option value="">Estado: automático</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button type="button" style={btn(true)} disabled={busy} onClick={send}>
          Enviar
        </button>
      </div>
    </div>
  );
}

const QUEUES = {
  esperando: 'Esperan respuesta (el último mensaje es del usuario)',
  escalados: 'Chat con persona: escalados y sin cerrar',
} as const;
type Queue = keyof typeof QUEUES;

export function SupportPage(): ReactElement {
  // Desde el Panel (27/10): `?filtro=esperando` o `?filtro=escalados` además de las colas por estado.
  const [params, setParams] = useSearchParams();
  const f = params.get('filtro');
  const queue: Queue | null = f === 'esperando' || f === 'escalados' ? f : null;
  const [status, setStatusRaw] = useState<Status>('open');
  const setStatus = (s: Status): void => {
    setParams({}, { replace: true });
    setStatusRaw(s);
  };
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<Doc | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    setRows(null);
    const base = collection(db, 'supportTickets');
    getDocs(
      queue === 'esperando'
        ? query(
            base,
            where('status', 'in', ['open', 'in_progress', 'waiting_user']),
            where('last_reply_by', '==', 'user'),
            limit(200),
          )
        : queue === 'escalados'
          ? query(
              base,
              where('status', 'in', ['open', 'in_progress']),
              where('bot_active', '==', false),
              limit(200),
            )
          : query(base, where('status', '==', status), orderBy('updated_at', 'asc'), limit(200)),
    )
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [status, version, queue]);
  return (
    <>
      <h1 style={{ margin: 0 }}>Soporte</h1>
      <p style={muted}>
        Promesa: respuesta humana en menos de 24 h. «Abiertos» son los que esperan al equipo (el bot
        ya escaló o el usuario pidió una persona). Las cifras públicas salen de
        `settings/supportStats`.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(Object.keys(QUEUES) as Queue[]).map((q) => (
          <button
            key={q}
            type="button"
            title={QUEUES[q]}
            style={btn(queue === q)}
            onClick={() => {
              setParams({ filtro: q }, { replace: true });
              setActive(null);
            }}
          >
            {q === 'esperando' ? 'Esperan respuesta' : 'Chat con persona'}
          </button>
        ))}
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            style={btn(!queue && status === s)}
            onClick={() => {
              setStatus(s);
              setActive(null);
            }}
          >
            {s}
          </button>
        ))}
      </div>
      <Notice text={error} error />
      {active && (
        <Thread
          ticket={active}
          onDone={() => {
            setVersion((v) => v + 1);
          }}
        />
      )}
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Asunto', 'Categoría', 'Idioma', 'Bot', 'Mensajes', 'Último', 'Creado', '']}>
          {rows.map((tk) => (
            <tr key={str(tk.id)}>
              <td style={td}>{str(tk.subject_es) || str(tk.subject)}</td>
              <td style={td}>{str(tk.category)}</td>
              <td style={td}>{str(tk.language)}</td>
              <td style={td}>{tk.bot_active === true ? 'sí' : 'no'}</td>
              <td style={td}>{str(tk.messages_count)}</td>
              <td style={td}>
                {str(tk.last_reply_by)} · {when(tk.last_reply_at)}
              </td>
              <td style={td}>{when(tk.created_at)}</td>
              <td style={td}>
                <button
                  type="button"
                  style={btn(false)}
                  onClick={() => {
                    setActive(tk);
                  }}
                >
                  Abrir
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={8}>
                Nada en esta cola.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}
